<template>
    <component
        :is="componentType"
        :href="href"
        :type="!href ? type : undefined"
        :disabled="isDisabled"
        :class="buttonClasses"
        :aria-disabled="isDisabled"
        :aria-busy="loading"
        v-bind="$attrs"
    >
        <!-- Loading Spinner -->
        <span v-if="loading" class="inline-flex" :class="iconSpacing">
            <svg
                class="animate-spin"
                :class="spinnerSize"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
        </span>

        <!-- Left Icon Slot -->
        <span v-if="$slots['icon-left'] && !loading" class="inline-flex" :class="iconSpacing">
            <slot name="icon-left" />
        </span>

        <!-- Button Content -->
        <span class="inline-flex items-center">
            <slot />
        </span>

        <!-- Right Icon Slot -->
        <span v-if="$slots['icon-right']" class="inline-flex" :class="iconSpacing">
            <slot name="icon-right" />
        </span>
    </component>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
    // Variant type
    variant: {
        type: String,
        default: 'primary',
        validator: (value) => ['primary', 'secondary', 'success', 'danger', 'warning', 'ghost'].includes(value),
    },
    // Size
    size: {
        type: String,
        default: 'md',
        validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value),
    },
    // Button type (button, submit, reset)
    type: {
        type: String,
        default: 'button',
        validator: (value) => ['button', 'submit', 'reset'].includes(value),
    },
    // Disabled state
    disabled: {
        type: Boolean,
        default: false,
    },
    // Loading state
    loading: {
        type: Boolean,
        default: false,
    },
    // Full width
    fullWidth: {
        type: Boolean,
        default: false,
    },
    // Rounded style
    rounded: {
        type: String,
        default: 'md',
        validator: (value) => ['none', 'sm', 'md', 'lg', 'full'].includes(value),
    },
    // Link href (makes it an Inertia Link)
    href: {
        type: String,
        default: null,
    },
});

// Component type (button or Link)
const componentType = computed(() => {
    return props.href ? Link : 'button';
});

// Is disabled
const isDisabled = computed(() => {
    return props.disabled || props.loading;
});

// Base classes
const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-secondary-900';

// Variant classes
const variantClasses = computed(() => {
    const variants = {
        primary: 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 focus:ring-primary-500 dark:bg-primary-500 dark:hover:bg-primary-600 dark:active:bg-primary-700',
        secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 active:bg-secondary-800 focus:ring-secondary-500 dark:bg-secondary-500 dark:hover:bg-secondary-600 dark:active:bg-secondary-700',
        success: 'bg-success-600 text-white hover:bg-success-700 active:bg-success-800 focus:ring-success-500 dark:bg-success-500 dark:hover:bg-success-600 dark:active:bg-success-700',
        danger: 'bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-800 focus:ring-danger-500 dark:bg-danger-500 dark:hover:bg-danger-600 dark:active:bg-danger-700',
        warning: 'bg-warning-600 text-white hover:bg-warning-700 active:bg-warning-800 focus:ring-warning-500 dark:bg-warning-500 dark:hover:bg-warning-600 dark:active:bg-warning-700',
        ghost: 'bg-transparent text-secondary-700 hover:bg-secondary-100 active:bg-secondary-200 focus:ring-secondary-500 dark:text-secondary-300 dark:hover:bg-secondary-800 dark:active:bg-secondary-700',
    };
    return variants[props.variant];
});

// Size classes
const sizeClasses = computed(() => {
    const sizes = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-base',
        lg: 'px-6 py-3 text-lg',
        xl: 'px-8 py-4 text-xl',
    };
    return sizes[props.size];
});

// Rounded classes
const roundedClasses = computed(() => {
    const rounded = {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-lg',
        lg: 'rounded-xl',
        full: 'rounded-full',
    };
    return rounded[props.rounded];
});

// Width class
const widthClass = computed(() => {
    return props.fullWidth ? 'w-full' : '';
});

// Disabled/loading class
const disabledClass = computed(() => {
    return isDisabled.value ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
});

// Combined button classes
const buttonClasses = computed(() => {
    return [
        baseClasses,
        variantClasses.value,
        sizeClasses.value,
        roundedClasses.value,
        widthClass.value,
        disabledClass.value,
    ].filter(Boolean).join(' ');
});

// Spinner size based on button size
const spinnerSize = computed(() => {
    const sizes = {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6',
        xl: 'h-7 w-7',
    };
    return sizes[props.size];
});

// Icon spacing
const iconSpacing = computed(() => {
    const spacing = {
        sm: '-ml-0.5 mr-1.5',
        md: '-ml-1 mr-2',
        lg: '-ml-1 mr-2.5',
        xl: '-ml-1.5 mr-3',
    };
    return spacing[props.size];
});
</script>

<style scoped>
/* Smooth transitions */
* {
    transition-property: color, background-color, border-color, opacity, box-shadow, transform;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 200ms;
}

/* Active state scale effect */
button:active:not(:disabled),
a:active:not([aria-disabled="true"]) {
    transform: scale(0.98);
}

/* Loading spinner animation */
@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style>
